/**
 * Utilities for extracting source file/field and target table/field
 * option lists from a MappingResponse's metadata and mappings.
 */

import type { MappingResponse } from '../../services/api';

export interface SourceOptions {
  files: string[];
  fieldsByFile: Record<string, string[]>;
}

export interface TargetOptions {
  tables: string[];
  fieldsByTable: Record<string, string[]>;
}

export function extractSourceOptions(mappingResponse?: MappingResponse): SourceOptions {
  if (!mappingResponse) return { files: [], fieldsByFile: {} };
  const metadata = mappingResponse.metadata as Record<string, unknown> | undefined;
  const fieldsByFile: Record<string, string[]> = {};
  const files: string[] = [];

  const snapshot = (metadata as Record<string, Record<string, unknown>> | undefined)?.source_schema_set_snapshot as Record<string, unknown> | undefined;
  if (snapshot?.tables) {
    Object.entries(snapshot.tables as Record<string, Record<string, unknown>>).forEach(([tableName, tableDef]) => {
      files.push(tableName);
      const cols = tableDef?.columns;
      if (Array.isArray(cols)) {
        fieldsByFile[tableName] = cols.map((c: Record<string, unknown>) => c.name as string).filter(Boolean);
      } else if (cols && typeof cols === 'object') {
        fieldsByFile[tableName] = Object.keys(cols as Record<string, unknown>);
      }
    });
  }

  if (files.length === 0) {
    const signature = (metadata as Record<string, Record<string, unknown>> | undefined)?.source_schema_signature as Record<string, unknown> | undefined;
    const sources = (signature?.sources ?? signature) as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(sources)) {
      sources.forEach((src) => {
        const sourceName = (src.source || src.name) as string;
        if (sourceName) {
          files.push(sourceName);
          const cols = src.columns;
          if (Array.isArray(cols)) {
            fieldsByFile[sourceName] = cols.map((c: Record<string, unknown>) => ((c.name || c.column_name) as string)).filter(Boolean);
          }
        }
      });
    }
  }

  if (files.length === 0 && Array.isArray(mappingResponse.mappings)) {
    const seen = new Set<string>();
    mappingResponse.mappings.forEach((m) => {
      const sf = m.source_file?.trim();
      if (sf && !seen.has(sf)) {
        seen.add(sf);
        files.push(sf);
      }
    });
    files.forEach((f) => {
      const fields = new Set<string>();
      mappingResponse.mappings?.forEach((m) => {
        if (m.source_file?.trim() === f && m.source_field?.trim()) {
          fields.add(m.source_field.trim());
        }
      });
      fieldsByFile[f] = Array.from(fields);
    });
  }

  return { files, fieldsByFile };
}

export function extractTargetOptions(mappingResponse?: MappingResponse): TargetOptions {
  if (!mappingResponse) return { tables: [], fieldsByTable: {} };
  const fieldsByTable: Record<string, string[]> = {};
  const tables: string[] = [];
  const seen = new Set<string>();

  mappingResponse.mappings?.forEach((m) => {
    const tt = m.target_table?.trim();
    if (tt && !seen.has(tt)) {
      seen.add(tt);
      tables.push(tt);
    }
  });

  tables.forEach((t) => {
    const fields = new Set<string>();
    mappingResponse.mappings?.forEach((m) => {
      if (m.target_table?.trim() === t && m.target_field?.trim()) {
        fields.add(m.target_field.trim());
      }
    });
    fieldsByTable[t] = Array.from(fields);
  });

  return { tables, fieldsByTable };
}
